import { Anchor } from "@mantine/core";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { AuthFlowScaffold } from "../auth/components/AuthFlowScaffold";
import { useAuthFlow } from "../auth/hooks/useAuthFlow";
import { safeReturnTo } from "../auth/rules/returnTo";

/**
 * recoverySearchSchema validates the /recovery search params. flow is the
 * recovery flow id; code is the deep-link code from the recovery email.
 */
const recoverySearchSchema = z.object({
  code: z.string().optional(),
  flow: z.string().optional(),
  return_to: z.string().optional(),
});

/**
 * Route is /recovery. Same two-step shape as verification (enter email → enter
 * code). On success Ory issues a privileged session + continue_with:
 * show_settings_ui to set a new password; the engine routes that to
 * /account/profile?flow=<id> (the SPA's settings entry point). Cross-link to
 * login.
 */
export const Route = createFileRoute("/_auth/recovery")({
  component: RecoveryRoute,
  validateSearch: recoverySearchSchema,
});

function RecoveryRoute() {
  const { t } = useTranslation("iam");
  const search = Route.useSearch();
  const state = useAuthFlow("recovery", {
    defaultRedirect: safeReturnTo(search.return_to),
    flowId: search.flow,
  });

  return (
    <AuthFlowScaffold state={state} subtitle={t("recovery.subtitle")} title={t("recovery.title")}>
      <Anchor component={Link} size="sm" to="/login">
        {t("recovery.backToSignIn")}
      </Anchor>
    </AuthFlowScaffold>
  );
}
